import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { FileText, Image as ImageIcon, FileArchive, Folder, Trash, UploadSimple, CaretRight, X, FloppyDisk } from '@phosphor-icons/react';

const isTextFile = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  return ['html', 'css', 'js', 'json', 'txt', 'md', 'xml', 'csv'].includes(ext);
};

const getFileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext)) return <ImageIcon size={20} className="text-blue-400" />;
  if (['zip', 'tar', 'gz'].includes(ext)) return <FileArchive size={20} className="text-yellow-400" />;
  return <FileText size={20} className="text-slate-300" />;
};

const BucketManager = () => {
  const { id } = useParams();
  const [deployment, setDeployment] = useState(null);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [prefix, setPrefix] = useState("");
  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [editFile, setEditFile] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDeployment();
  }, [id]);

  useEffect(() => {
    if (deployment) {
      fetchFiles();
    }
  }, [deployment, prefix]);

  const fetchDeployment = async () => {
    try {
      const res = await axios.get(`/api/deployments/${id}`, { withCredentials: true });
      setDeployment(res.data);
    } catch (e) {
      toast.error("Failed to load deployment details");
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/deployments/${id}/files?prefix=${encodeURIComponent(prefix)}`, { withCredentials: true });
      setFolders(res.data.folders);
      setFiles(res.data.files);
    } catch (e) {
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('prefix', prefix);

    try {
      await axios.post(`/api/deployments/${id}/files/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("File uploaded");
      fetchFiles();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete ${key}?`)) return;
    try {
      await axios.delete(`/api/deployments/${id}/files?key=${encodeURIComponent(key)}`, { withCredentials: true });
      toast.success("File deleted");
      fetchFiles();
    } catch (err) {
      toast.error("Failed to delete file");
    }
  };

  const openEditor = async (fileObj) => {
    if (!isTextFile(fileObj.name)) {
      if (deployment.public_url) {
        // Construct file URL
        const url = deployment.public_url.replace('/index.html', '') + '/' + fileObj.key;
        window.open(url, '_blank');
      } else {
        toast.error("Cannot edit this file type directly.");
      }
      return;
    }

    try {
      const res = await axios.get(`/api/deployments/${id}/files/content?key=${encodeURIComponent(fileObj.key)}`, { withCredentials: true });
      setEditContent(res.data.content);
      setEditFile(fileObj);
    } catch (err) {
      toast.error("Failed to load file content");
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/deployments/${id}/files/content`, {
        key: editFile.key,
        content: editContent
      }, { withCredentials: true });
      toast.success("File saved");
      setEditFile(null);
    } catch (err) {
      toast.error("Failed to save file");
    } finally {
      setSaving(false);
    }
  };

  const navigateTo = (newPrefix) => {
    setPrefix(newPrefix);
  };

  const renderBreadcrumbs = () => {
    const parts = prefix.split('/').filter(Boolean);
    let currentPath = '';

    return (
      <div className="flex items-center gap-2 text-sm font-mono text-slate-400 mb-6 bg-black/20 p-4 border border-white/10">
        <button onClick={() => navigateTo('')} className="hover:text-white transition-colors">root</button>
        {parts.map((p, idx) => {
          currentPath += p + '/';
          const isLast = idx === parts.length - 1;
          const targetPath = currentPath;
          return (
            <React.Fragment key={targetPath}>
              <CaretRight size={14} className="text-slate-600" />
              <button 
                onClick={() => !isLast && navigateTo(targetPath)} 
                className={`${isLast ? 'text-white font-bold' : 'hover:text-white transition-colors'}`}
              >
                {p}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (!deployment) return <div className="p-8 text-center text-slate-400">Loading deployment info...</div>;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black mb-2 uppercase">FILE MANAGER</h1>
          <p className="text-slate-400 font-mono text-sm">Bucket: <span className="text-white">{deployment.bucket_name}</span></p>
        </div>
        <div className="flex gap-4">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleUpload} 
            className="hidden" 
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-primary text-black px-4 py-2 font-bold uppercase tracking-wider hover:bg-[#00B8CC] transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <UploadSimple size={20} />
            {uploading ? 'UPLOADING...' : 'UPLOAD FILE'}
          </button>
        </div>
      </div>

      {renderBreadcrumbs()}

      <div className="border border-white/10 bg-card overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-black/40">
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-32">Size</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-48">Last Modified</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="4" className="p-8 text-center text-slate-500">Loading files...</td></tr>}
            
            {!loading && prefix !== "" && (
              <tr className="border-b border-white/5 hover:bg-white/5 transition-colors group cursor-pointer" onClick={() => {
                const parts = prefix.split('/').filter(Boolean);
                parts.pop();
                navigateTo(parts.length ? parts.join('/') + '/' : '');
              }}>
                <td className="p-4 flex items-center gap-3 font-mono text-sm">
                  <Folder size={20} className="text-slate-500" weight="fill" />
                  <span>..</span>
                </td>
                <td className="p-4 text-sm text-slate-500">-</td>
                <td className="p-4 text-sm text-slate-500">-</td>
                <td className="p-4"></td>
              </tr>
            )}

            {!loading && folders.map(folder => {
              const folderName = folder.replace(prefix, '').replace('/', '');
              return (
                <tr key={folder} className="border-b border-white/5 hover:bg-white/5 transition-colors group cursor-pointer" onClick={() => navigateTo(folder)}>
                  <td className="p-4 flex items-center gap-3 font-mono text-sm">
                    <Folder size={20} className="text-[#00E5FF]" weight="fill" />
                    <span className="font-bold text-white">{folderName}</span>
                  </td>
                  <td className="p-4 text-sm text-slate-500">-</td>
                  <td className="p-4 text-sm text-slate-500">-</td>
                  <td className="p-4 text-right"></td>
                </tr>
              );
            })}

            {!loading && files.map(f => (
              <tr key={f.key} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                <td className="p-4 flex items-center gap-3 font-mono text-sm cursor-pointer" onClick={() => openEditor(f)}>
                  {getFileIcon(f.name)}
                  <span className={`${isTextFile(f.name) ? 'hover:text-primary' : 'text-slate-300'}`}>{f.name}</span>
                </td>
                <td className="p-4 text-sm font-mono text-slate-400">{(f.size / 1024).toFixed(1)} KB</td>
                <td className="p-4 text-sm font-mono text-slate-500">{format(new Date(f.last_modified), 'yyyy-MM-dd HH:mm')}</td>
                <td className="p-4 text-right">
                  <button onClick={() => handleDelete(f.key)} className="text-slate-500 hover:text-red-500 transition-colors p-2 opacity-0 group-hover:opacity-100">
                    <Trash size={18} />
                  </button>
                </td>
              </tr>
            ))}
            
            {!loading && folders.length === 0 && files.length === 0 && prefix === "" && (
              <tr><td colSpan="4" className="p-8 text-center text-slate-500">This bucket is empty.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editFile && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col animate-in fade-in duration-200">
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0A0B0E]">
            <div className="flex items-center gap-4">
              <h2 className="font-bold font-mono text-lg text-primary">{editFile.key}</h2>
              {saving && <span className="text-yellow-400 text-xs font-bold animate-pulse">Saving...</span>}
            </div>
            <div className="flex items-center gap-4">
              <button onClick={saveContent} disabled={saving} className="bg-[#39FF14] text-black px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#39FF14]/80 disabled:opacity-50">
                <FloppyDisk size={18} /> Save Changes
              </button>
              <button onClick={() => setEditFile(null)} className="text-slate-400 hover:text-white p-2">
                <X size={24} />
              </button>
            </div>
          </div>
          <div className="flex-1 p-6 relative">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="absolute inset-4 bg-[#13151A] text-[#A0AEC0] font-mono p-6 resize-none border border-white/10 focus:outline-none focus:border-primary transition-colors leading-relaxed"
              spellCheck="false"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BucketManager;